import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const galleryImages = [
  {
    src: "https://static.prod-images.emergentagent.com/jobs/ccab85ca-bf95-4b72-aac4-cda44cdf7c03/images/761bbfe777316c3c69fe07841a67965cfeb0925786d8149fbffd2912c28f926e.png",
    alt: "Aircraft engine turbine close-up",
    caption: "Precision Engineering",
    size: "large",
  },
  {
    src: "https://images.unsplash.com/photo-1559268950-2d7ceb2efa3a?w=800&q=80",
    alt: "Commercial airplane in flight",
    caption: "Commercial Aviation",
    size: "medium",
  },
  {
    src: "https://images.unsplash.com/photo-1485310818226-f01c4269687f?w=800&q=80",
    alt: "Aviation mechanic working on engine",
    caption: "Maintenance Operations",
    size: "medium",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/ccab85ca-bf95-4b72-aac4-cda44cdf7c03/images/4190f3c52537707140d3008386eb697e3f1afcde485b5d9691ec0c87e530d1c2.png",
    alt: "Aircraft cleaning operations",
    caption: "Surface Treatment",
    size: "medium",
  },
];

const industries = [
  { name: "Commercial Airlines", desc: "Major domestic carriers requiring OEM-compliant cleaning solutions" },
  { name: "MRO Facilities", desc: "Maintenance, Repair, and Overhaul centers across India" },
  { name: "Charter Operations", desc: "Private and charter aviation companies" },
  { name: "Ground Handling", desc: "Airport ground handling and service providers" },
  { name: "Aerospace Manufacturing", desc: "Aircraft component and assembly facilities" },
  { name: "Defense Aviation", desc: "Military aviation maintenance applications" },
];

export default function GallerySection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 sm:py-32 bg-[#F7F4EC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Header */}
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="text-sm uppercase tracking-[0.2em] font-medium text-[#0077D1] mb-4">
            Industries & Operations
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.2] font-semibold text-[#0C3A72] mb-4">
            Serving Aviation Excellence
          </motion.h2>
          <motion.div variants={fadeUp} className="section-divider mx-auto" />
        </motion.div>

        {/* Industries Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-20">
          {industries.map((industry, idx) => (
            <motion.div
              key={industry.name}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              data-testid={`industry-card-${idx}`}
              className="bg-white border border-[#0C3A72]/10 p-6 hover:border-[#0077D1]/30 transition-all duration-300"
            >
              <h4 className="text-base font-semibold text-[#0C3A72] mb-2">{industry.name}</h4>
              <p className="text-sm text-[#4A627E]">{industry.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Gallery */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <p className="text-sm uppercase tracking-[0.2em] font-medium text-[#0077D1]">Operations Gallery</p>
            <div className="h-px flex-1 bg-[#0C3A72]/10" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((img, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + idx * 0.1 }}
                className={`img-zoom-container relative group ${
                  idx === 0 ? "col-span-2 row-span-2" : ""
                }`}
                data-testid={`gallery-image-${idx}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover aspect-square"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#0C3A72]/0 group-hover:bg-[#0C3A72]/40 transition-all duration-500 flex items-end">
                  <div className="p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-sm font-medium">{img.caption}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
